import React from 'react';
import { Link } from 'react-router-dom';
import SocialIcons from './SocialIcons';
import { getStoreUrl } from './StoreLinks';
import LiveStatus from './LiveStatus';
import SpecialHours from './SpecialHours';
import { SHOW_SUMMER_PASS } from '../utils/features';

export default function Footer() {
  return (
    <footer style={styles.footer}>
      <div style={styles.glowLine} />
      <div className="container" style={styles.inner}>
        <div style={styles.grid}>

          {/* Brand */}
          <div style={styles.brand}>
            <div style={styles.logoRow}>
              <img src="/assets/whapaxx-logo-transparent.png" alt="Whapaxx Gaming" style={styles.logoImg} />
              <span style={styles.logoText}>WHAPAXX GAMING</span>
            </div>
            <p style={styles.tagline}>
              The Future of Arcade Entertainment.
              Where technology meets play.
            </p>
            <SocialIcons />
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={styles.colTitle}>Navigate</h4>
            {[
              { to: '/games', label: 'Games' },
              { to: '/pricing', label: 'Pricing' },
              { to: '/game-day-grub', label: 'Game Day Grub' },
              { to: '/gift-cards', label: 'Gift Cards' },
              ...(SHOW_SUMMER_PASS ? [{ to: '/summer-pass', label: 'Summer Day Pass' }] : []),
              { to: '/faq', label: 'FAQ & Policies' },
              { to: '/privacy', label: 'Privacy Policy' },
            ].map(link => (
              <Link key={link.to} to={link.to} style={styles.footLink}>{link.label}</Link>
            ))}
          </div>

          {/* Contact */}
          <div>
            <h4 style={styles.colTitle}>Connect</h4>
            <p style={styles.contactLine}>info@whapaxxgaming.com</p>
            <p style={styles.contactLine}>Doric House</p>
            <p style={styles.contactLine}>Mighty Grynner H'way</p>
            <p style={styles.contactLine}>St. Michael, Barbados</p>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-neon" style={{ padding: '0.5rem 1.2rem', fontSize: '0.7rem' }}>
                <span>Get in Touch</span>
              </Link>
              {/* Opens the App Store or Google Play for the visitor's phone. */}
              <a href="https://apps.apple.com/us/app/whapaxx-gaming/id6514299783"
                onClick={(e) => { e.preventDefault(); window.open(getStoreUrl(), '_blank'); }}
                className="btn-neon footer-app-btn" style={{ padding: '0.5rem 1.2rem', fontSize: '0.7rem', textDecoration: 'none' }}>
                <span>Download App</span>
              </a>
              <a href="https://maps.app.goo.gl/kpoU67WHgKKT1rX96" target="_blank" rel="noopener noreferrer" className="btn-neon btn-neon--filled" style={{ padding: '0.5rem 1.2rem', fontSize: '0.7rem', textDecoration: 'none' }}>
                <span>Get Directions</span>
              </a>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h4 style={styles.colTitle}>Operating Hours</h4>
            <div style={{ marginBottom: '0.75rem' }}>
              <LiveStatus />
            </div>
            <p style={styles.contactLine}>Mon, Tue, Thu: 4 PM – 10 PM</p>
            <p style={styles.contactLine}>Wed: 4 PM – 12 AM</p>
            <p style={styles.contactLine}>Fri: 4 PM – 2 AM</p>
            <p style={styles.contactLine}>Sat: 2 PM – 2 AM</p>
            <p style={styles.contactLine}>Sun: 2 PM – 10 PM</p>
            <SpecialHours variant="compact" />
            <h4 style={{ ...styles.colTitle, marginTop: '1rem' }}>Kitchen Hours</h4>
            <p style={styles.contactLine}>Wed – Thu: 4 PM – 9:30 PM</p>
            <p style={styles.contactLine}>Fri – Sat: 4 PM – 11:30 PM</p>
            <p style={styles.contactLine}>Sun: 2 PM – 9:30 PM</p>
            <p style={styles.contactLine}>Open Holidays</p>
          </div>
        </div>

        <div style={styles.bottom}>
          <p style={styles.copy}>© {new Date().getFullYear()} Whapaxx Gaming. All rights reserved.</p>
          <p style={styles.copy}>Powered by technology. Fueled by play.</p>
        </div>
      </div>
    </footer>
  );
}

const styles = {
  footer: {
    background: 'linear-gradient(180deg, #05081f 0%, #080d30 100%)',
    paddingTop: '0',
    paddingBottom: '2rem',
    position: 'relative',
  },
  glowLine: {
    height: '1px',
    background: 'linear-gradient(90deg, transparent, #12d0ff, #ff2d78, transparent)',
    boxShadow: '0 0 15px rgba(18, 208, 255, 0.4)',
    marginBottom: '3rem',
  },
  inner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 clamp(1rem, 4vw, 2rem)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '2rem',
    marginBottom: '3rem',
  },
  brand: {},
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  logoImg: {
    width: '36px',
    height: '36px',
    objectFit: 'contain',
  },
  logoText: {
    fontFamily: "'Orbitron', sans-serif",
    fontWeight: 700,
    fontSize: '0.85rem',
    letterSpacing: '0.15em',
    color: '#eaf2ff',
  },
  tagline: {
    color: '#a8b2ff',
    fontSize: '0.9rem',
    lineHeight: 1.6,
    marginBottom: '1rem',
  },
  socialRow: {
    display: 'flex',
    gap: '1rem',
  },
  socialLink: {
    fontFamily: "'Rajdhani', sans-serif",
    fontWeight: 600,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#12d0ff',
    textDecoration: 'none',
    transition: 'text-shadow 0.3s',
  },
  colTitle: {
    fontFamily: "'Orbitron', sans-serif",
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.2em',
    color: '#eaf2ff',
    marginBottom: '1rem',
  },
  footLink: {
    display: 'block',
    fontFamily: "'Rajdhani', sans-serif",
    fontWeight: 500,
    fontSize: '0.95rem',
    color: '#a8b2ff',
    textDecoration: 'none',
    marginBottom: '0.5rem',
    transition: 'color 0.3s',
  },
  contactLine: {
    color: '#a8b2ff',
    fontSize: '0.9rem',
    marginBottom: '0.4rem',
  },
  bottom: {
    borderTop: '1px solid rgba(18, 208, 255, 0.1)',
    paddingTop: '1.5rem',
    textAlign: 'center',
  },
  copy: {
    color: '#7881c0',
    fontSize: '0.8rem',
    marginBottom: '0.25rem',
  },
};
